import { Chip } from '@ipfabric/ui-components/Chip/Chip';
import { severityDots } from '@ipfabric/ui-components/IntentFilterSelect/IntentFilterSelect.styles';
import { Link } from '@ipfabric/ui-components/Link/Link';
import {
  type ReportSeverityKey,
  type TableFilter,
  isBooleanTableFilter,
  isObjectTableFilter,
  isSelectTableFilter,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { createSelector } from 'reselect';
import { createScopedSelector } from 'utils/react/scopedSelector';
import {
  type TableMergedState,
  useTableContextSelectors,
} from '../model/TableModel';
import { selectColumnsByKey } from '../model/selectors/selectColumnsByKey';
import { getOperatorFromFilter } from './SimpleFilter/helpers/getOperatorFromFilter';
import type { SimpleFilterField } from './SimpleFilter/types/SimpleFilterField';

const isFilterFieldEmpty = (field: SimpleFilterField) =>
  field.value === null ||
  field.value === '' ||
  (Array.isArray(field.value) && field.value.length === 0);

/**
 * Get Object.entries of simpleFilterFieldsSubmitted filtered from fields that are submitted as empty.
 */
const selectActiveSimpleFilterFields = createScopedSelector<
  TableMergedState,
  [string, SimpleFilterField][]
>(() =>
  createSelector(
    prop('simpleFilterFieldsSubmitted'),
    (simpleFilterFieldsSubmitted) =>
      Object.entries(simpleFilterFieldsSubmitted ?? {}).filter(
        ([, field]) => !isFilterFieldEmpty(field),
      ),
  ),
);

/**
 * Get Object.entries of simpleFilterFieldsSubmitted filtered from fields that are submitted as empty.
 */
const selectActiveIntentFilters = createScopedSelector<
  TableMergedState,
  [string, ReportSeverityKey][]
>(() =>
  createSelector(prop('intentFilters'), (intentFilters) =>
    Object.entries(intentFilters ?? {}).filter(
      (arg): arg is [string, ReportSeverityKey] => arg[1] !== null,
    ),
  ),
);

const getSimpleFilterOperatorAndValue = (
  simpleFilterField: SimpleFilterField,
  leafFilter: TableFilter,
) => {
  if (Array.isArray(simpleFilterField.value)) {
    return { readableOperator: '', readableValue: 'is one of selected' };
  }
  const readableOperator = getOperatorFromFilter(
    leafFilter,
    simpleFilterField.value,
  );
  let readableValue =
    simpleFilterField.value?.substring(readableOperator.length) ?? '(empty)';

  if (isSelectTableFilter(leafFilter) || isBooleanTableFilter(leafFilter)) {
    readableValue =
      leafFilter.options?.find((o) => o.value === readableValue)?.label ??
      readableValue;
  }
  return { readableOperator, readableValue };
};

export const AppliedFilters = () => {
  const ctxProps = useTableContextSelectors({
    activeSimpleFilterFields: selectActiveSimpleFilterFields,
    activeIntentFilters: selectActiveIntentFilters,
    columnsByKey: selectColumnsByKey,
    externalFilters: prop('externalFilters'),
    onChangeFilter: prop('onChangeFilter'),
    onChangeIntentFilter: prop('onChangeIntentFilter'),
    onRemoveFilters: prop('onRemoveFilters'),
    onSubmitFilters: prop('onSubmitFilters'),
    onSelectFilterPreset: prop('onSelectFilterPreset'),
  });

  const renderSimpleFilterBadge = (
    simpleFilterField: SimpleFilterField,
    key: string,
  ) => {
    const objectFilterChild =
      simpleFilterField.child && isObjectTableFilter(simpleFilterField.filter)
        ? simpleFilterField.filter.children.find(
            (child) => child.field === simpleFilterField.child,
          )
        : null;
    // filter of the end value - child for an object, self for others
    const leafFilter = objectFilterChild?.filter ?? simpleFilterField.filter;
    const { readableOperator, readableValue } = getSimpleFilterOperatorAndValue(
      simpleFilterField,
      leafFilter,
    );

    return (
      <Chip
        key={key}
        variant='outline'
        size='small'
        onRemove={
          ctxProps.onChangeFilter &&
          ctxProps.onSubmitFilters &&
          (() => {
            ctxProps.onChangeFilter?.({
              column: key,
              value: null,
              child: null,
            });
            ctxProps.onSubmitFilters?.();
          })
        }
      >
        {ctxProps.columnsByKey[key]?.title ?? key}
        {objectFilterChild && ` | ${objectFilterChild.title}`}
        {readableOperator ? ` ${readableOperator} ` : ': '}
        <strong>{readableValue}</strong>
      </Chip>
    );
  };

  const renderIntentFilterBadge = (
    severity: ReportSeverityKey,
    key: string,
  ) => {
    return (
      <Chip
        key={key}
        variant='outline'
        size='small'
        onRemove={
          ctxProps.onChangeIntentFilter &&
          (() => {
            ctxProps.onChangeIntentFilter?.({
              column: key,
              severity: null,
            });
          })
        }
      >
        <div className='flex flex-row items-center gap-3xs'>
          {`${ctxProps.columnsByKey[key]?.title ?? key}: `}
          <div className='size-3'>{severityDots[severity]}</div>
        </div>
      </Chip>
    );
  };

  if (
    (ctxProps.externalFilters?.length ?? 0) === 0 &&
    ctxProps.activeSimpleFilterFields.length === 0 &&
    ctxProps.activeIntentFilters.length === 0
  ) {
    return null;
  }

  return (
    <div className='ipf-table-applied-filters px flex flex-row gap-xs'>
      {ctxProps.externalFilters?.map((f) => (
        <Chip
          key={f.key}
          variant='outline'
          size='small'
          onRemove={
            ctxProps.onRemoveFilters &&
            (() => {
              ctxProps.onRemoveFilters?.({ externalFilterKeys: [f.key] });
              // Clear selected preset from 'Select filter' selectbox
              ctxProps.onSelectFilterPreset?.(null);
            })
          }
        >
          {f.label}
        </Chip>
      ))}
      {ctxProps.activeSimpleFilterFields.map(([key, simpleFilterField]) =>
        renderSimpleFilterBadge(simpleFilterField, key),
      )}
      {ctxProps.activeIntentFilters.map(([key, intentFilter]) =>
        renderIntentFilterBadge(intentFilter, key),
      )}
      {ctxProps.onChangeFilter && ctxProps.onSubmitFilters && (
        <>
          <div className='w-0 border-l border-solid border-divider' />
          <Link
            renderAs='button'
            onClick={() => {
              ctxProps.onRemoveFilters?.();
              // Clear selected preset from 'Select filter' selectbox
              ctxProps.onSelectFilterPreset?.(null);
            }}
          >
            Clear all
          </Link>
        </>
      )}
    </div>
  );
};
